import React, { useState, useEffect } from 'react';
import './BookingList.css';
import apiClient from '../../api';
import { formatShortDate, formatTime } from '../../utils/dateUtils';

const BookingList = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedUsers, setExpandedUsers] = useState({});

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/api/booking/admin/all/');
            const data = response.data;

            if (data.success && Array.isArray(data.bookings)) {
                setBookings(data.bookings);
            } else {
                setBookings([]);
            }
            setError(null);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            setError('Failed to load bookings');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (bookingId, newStatus) => {
        if (!window.confirm(`Are you sure you want to mark this booking as ${newStatus}?`)) return;
        try {
            await apiClient.put(`/api/booking/admin/${bookingId}/status/`, { status: newStatus });
            fetchBookings();
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update booking status');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const hour12 = hour % 12 || 12;
            return `${hour12}:${minutes} ${ampm}`;
        } catch {
            return timeString;
        }
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed':
                return 'status-confirmed';
            case 'pending':
                return 'status-pending';
            case 'cancelled':
                return 'status-cancelled';
            case 'completed':
                return 'status-completed';
            default:
                return 'status-pending';
        }
    };

    const handleCardFilter = (status) => {
        setStatusFilter(prev => prev === status ? 'all' : status);
    };

    const toggleUserExpand = (userKey) => {
        setExpandedUsers(prev => ({
            ...prev,
            [userKey]: !prev[userKey]
        }));
    };

    const filteredBookings = bookings.filter(booking => {
        const matchesSearch =
            (booking.user_name && booking.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.user_email && booking.user_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.pandit_name && booking.pandit_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.subject && booking.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.location && booking.location.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus = statusFilter === 'all' || booking.status?.toLowerCase() === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Group bookings by user email (or user_name if email not available)
    const groupedBookings = filteredBookings.reduce((groups, booking) => {
        const userKey = booking.user_email || booking.user_name || 'unknown';
        if (!groups[userKey]) {
            groups[userKey] = {
                user_name: booking.user_name || 'Unknown',
                user_email: booking.user_email || 'No email',
                bookings: []
            };
        }
        groups[userKey].bookings.push(booking);
        return groups;
    }, {});

    // Calculate stats from search-only filtered data (so counts stay correct regardless of status filter)
    const searchFilteredBookings = bookings.filter(booking => {
        if (!searchTerm) return true;
        return (
            (booking.user_name && booking.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.user_email && booking.user_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.pandit_name && booking.pandit_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.subject && booking.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (booking.location && booking.location.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    });

    const totalBookings = searchFilteredBookings.length;
    const pendingCount = searchFilteredBookings.filter(b => b.status?.toLowerCase() === 'pending').length;
    const confirmedCount = searchFilteredBookings.filter(b => b.status?.toLowerCase() === 'confirmed').length;
    const completedCount = searchFilteredBookings.filter(b => b.status?.toLowerCase() === 'completed').length;

    if (loading) {
        return <div className="loading-message">Loading bookings...</div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div className="booking-list">
            <div className="booking-header">
                <h1 className="booking-title">🛕 Puja Bookings</h1>
            </div>

            {/* Stats Cards */}
            <div className="booking-stats">
                <div
                    className={`stat-card stat-total clickable-card ${statusFilter === 'all' ? 'active-card' : ''}`}
                    onClick={() => setStatusFilter('all')}
                    title="Click to show all bookings"
                >
                    <div className="stat-icon">📋</div>
                    <div className="stat-content">
                        <div className="stat-value">{totalBookings}</div>
                        <div className="stat-label">Total Bookings</div>
                    </div>
                </div>
                <div
                    className={`stat-card stat-pending-card clickable-card ${statusFilter === 'pending' ? 'active-card' : ''}`}
                    onClick={() => handleCardFilter('pending')}
                    title="Click to filter Pending bookings"
                >
                    <div className="stat-icon">⏳</div>
                    <div className="stat-content">
                        <div className="stat-value">{pendingCount}</div>
                        <div className="stat-label">Pending</div>
                    </div>
                </div>
                <div
                    className={`stat-card stat-confirmed-card clickable-card ${statusFilter === 'confirmed' ? 'active-card' : ''}`}
                    onClick={() => handleCardFilter('confirmed')}
                    title="Click to filter Confirmed bookings"
                >
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                        <div className="stat-value">{confirmedCount}</div>
                        <div className="stat-label">Confirmed</div>
                    </div>
                </div>
                <div
                    className={`stat-card stat-completed-card clickable-card ${statusFilter === 'completed' ? 'active-card' : ''}`}
                    onClick={() => handleCardFilter('completed')}
                    title="Click to filter Completed bookings"
                >
                    <div className="stat-icon">🎉</div>
                    <div className="stat-content">
                        <div className="stat-value">{completedCount}</div>
                        <div className="stat-label">Completed</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-container">
                <input
                    type="text"
                    placeholder="🔍 Search by user, pandit, puja, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="status-filter"
                >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            {filteredBookings.length === 0 && bookings.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🛕</div>
                    <h3 className="empty-title">No Bookings Found</h3>
                    <p className="empty-description">
                        No puja bookings have been made yet. Bookings will appear here once users make them.
                    </p>
                </div>
            ) : filteredBookings.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3 className="empty-title">No Matching Bookings</h3>
                    <p className="empty-description">
                        Try adjusting your search or filter to find bookings.
                    </p>
                </div>
            ) : (
                <div className="user-groups-container">
                    {Object.entries(groupedBookings).map(([userKey, userData]) => {
                        const isExpanded = expandedUsers[userKey];
                        const hasMultipleBookings = userData.bookings.length > 1;

                        return (
                            <div key={userKey} className="user-group">
                                <div
                                    className={`user-group-header ${hasMultipleBookings ? 'clickable' : ''}`}
                                    onClick={() => hasMultipleBookings && toggleUserExpand(userKey)}
                                >
                                    <div className="user-group-info">
                                        <div className="user-avatar-large">
                                            {userData.user_name ? userData.user_name.substring(0, 2).toUpperCase() : '??'}
                                        </div>
                                        <div className="user-group-details">
                                            <div className="user-group-name">{userData.user_name}</div>
                                            <div className="user-group-email">{userData.user_email}</div>
                                        </div>
                                        <div className="booking-count-badge">
                                            {userData.bookings.length} booking{userData.bookings.length > 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    {hasMultipleBookings && (
                                        <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                            ▼
                                        </div>
                                    )}
                                </div>

                                {/* Show bookings - always show if single booking, show if expanded for multiple */}
                                {(hasMultipleBookings ? isExpanded : true) && (
                                    <div className="user-bookings-table">
                                        <table className="booking-table">
                                            <thead>
                                                <tr>
                                                    <th>Puja/Subject</th>
                                                    <th>Pandit</th>
                                                    <th className="hide-mobile">Location</th>
                                                    <th>Date & Time</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userData.bookings.map((booking, index) => (
                                                    <tr key={booking.id || index}>
                                                        <td>
                                                            <span className="subject">{booking.subject || 'N/A'}</span>
                                                        </td>
                                                        <td>
                                                            <span className="pandit-name">🧘 {booking.pandit_name || 'Unknown'}</span>
                                                        </td>
                                                        <td className="hide-mobile">
                                                            <span className="location">📍 {booking.location || 'N/A'}</span>
                                                        </td>
                                                        <td>
                                                            <div className="datetime">
                                                                <div className="date">📅 {formatDate(booking.booking_date)}</div>
                                                                <div className="time">🕐 {formatTime(booking.booking_time)}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${getStatusClass(booking.status)}`}>
                                                                {booking.status || 'Pending'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                                <button
                                                                    onClick={() => alert(`Booking Details:\n\nUser: ${booking.user_name}\nEmail: ${booking.user_email}\nPuja: ${booking.subject}\nPandit: ${booking.pandit_name}\nLocation: ${booking.location}\nDate: ${formatDate(booking.booking_date)}\nTime: ${formatTime(booking.booking_time)}\nStatus: ${booking.status}\nCreated: ${formatDate(booking.created_at)}`)}
                                                                    className="view-button"
                                                                    title="View Details"
                                                                >
                                                                    👁️ View
                                                                </button>
                                                                <button
                                                                    onClick={() => handleStatusUpdate(booking.id || booking._id, 'confirmed')}
                                                                    title="Confirm Booking"
                                                                    style={{ background: '#28a745', color: 'white', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                                                                >
                                                                    ✅
                                                                </button>
                                                                <button
                                                                    onClick={() => handleStatusUpdate(booking.id || booking._id, 'rejected')}
                                                                    title="Reject Booking"
                                                                    style={{ background: '#dc3545', color: 'white', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                                                                >
                                                                    ❌
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default BookingList;
